import type { Catalog } from '../types';

/**
 * O assistente de registro de volta — cinco passos, mais o cartão do flight.
 *
 * É a tela mais concatenada do app, e por isso a que mais ganha em ter frases inteiras no catálogo.
 * Onde a mensagem só existia como pedaços costurados em JSX, ela virou **uma chave por frase**.
 */
export const record = {
  'record.step': { pt: 'Passo {step} de {total}', en: 'Step {step} of {total}' },
  'record.cancel': { pt: 'Cancelar', en: 'Cancel' },

  // ------------------------------------------------------------- passo 1: campo

  'record.step1.title': { pt: 'Onde você jogou?', en: 'Where did you play?' },
  'record.sortByDistance': { pt: 'Ordenar por distância', en: 'Sort by distance' },
  'record.noLocation': {
    pt: 'Sem acesso à localização — a lista fica em ordem alfabética. A busca continua funcionando.',
    en: 'No access to your location — the list stays alphabetical. Search still works.',
  },
  'record.myCourse': { pt: 'Meu campo', en: 'My course' },
  'record.nearYou': { pt: 'Próximos de você', en: 'Near you' },
  'record.results': { pt: 'Resultados', en: 'Results' },
  'record.noCourseFor': {
    pt: 'Nenhum campo encontrado para “{query}”.',
    en: 'No course found for “{query}”.',
  },
  'record.showMore': {
    pt: { one: 'Mostrar mais {count} campo', other: 'Mostrar os outros {count} campos' },
    en: { one: 'Show {count} more course', other: 'Show the other {count} courses' },
  },
  /** O rótulo da barra de ação, acima do nome escolhido. */
  'record.courseLabel': { pt: 'Campo', en: 'Course' },

  // ------------------------------------------------------ passo 2: configuração

  'record.loadingCourse': { pt: 'Carregando campo…', en: 'Loading course…' },
  /**
   * Num campo de 9 buracos jogado como 18, cada passagem é uma "volta" — e a API exige um tee para
   * cada uma. Em inglês o par consagrado é *first nine* / *second nine*.
   */
  'record.firstNine': { pt: 'Primeira volta', en: 'First nine' },
  'record.secondNine': { pt: 'Segunda volta', en: 'Second nine' },

  // -------------------------------------------------------- passo 4: scorecard

  'record.loadingRound': { pt: 'Carregando volta…', en: 'Loading round…' },

  // ---------------------------------------------------------- passo 5: revisar

  'record.review': { pt: 'Revisar', en: 'Review' },
  'record.reviewMeta': {
    pt: '{scored} de {holes} buracos preenchidos · {course}',
    en: '{scored} of {holes} holes filled in · {course}',
  },
  'record.finishWarningStrong': {
    pt: 'Finalizar recalcula seu handicap.',
    en: 'Finishing recalculates your handicap.',
  },
  'record.finishWarning': {
    pt: '{strong} Depois disso a volta não pode mais ser editada.',
    en: '{strong} After that the round can no longer be edited.',
  },
  'record.keepEditing': { pt: 'Continuar editando', en: 'Keep editing' },

  'record.apiBlock': {
    pt: 'Bloqueio da API — {scored} de {holes}',
    en: 'API block — {scored} of {holes}',
  },
  /**
   * O singular **nomeia** o buraco que falta; o plural só conta. São marcadores diferentes na mesma
   * entrada, e é por isso que `ParamNames` une os de **todas** as formas em vez de olhar só a
   * `other` — do contrário `{first}` ficaria sem tipo.
   */
  'record.missing': {
    pt: { one: 'Falta o buraco {first}', other: 'Faltam {count} buracos' },
    en: { one: 'Hole {first} is missing', other: '{count} holes missing' },
  },
  'record.apiRule': {
    pt: 'A volta declarou {holes} buracos e o servidor exige exatamente {holes} preenchidos.',
    en: 'The round declared {holes} holes and the server requires exactly {holes} filled in.',
  },
  'record.canClose9': {
    pt: ' Com 9 preenchidos, dá para fechar como 9.',
    en: ' With 9 filled in, it can be closed as 9.',
  },
  'record.cannotClose9': {
    pt: ' Fechar como 9 exige exatamente 9 — não serve para outras contagens.',
    en: ' Closing as 9 requires exactly 9 — it does not work for other counts.',
  },
  'record.goToHole': { pt: 'Ir para o buraco {number}', en: 'Go to hole {number}' },
  'record.closeAs9Holes': { pt: 'Fechar como 9 buracos', en: 'Close as 9 holes' },

  // ------------------------------------------------- o buraco, um de cada vez

  'record.saveHoleFailed': {
    pt: 'Não foi possível salvar o buraco {number}',
    en: 'Could not save hole {number}',
  },
  'record.retry': { pt: 'Tentar de novo', en: 'Try again' },
  'record.saving': { pt: 'Salvando', en: 'Saving' },
  'record.saveError': { pt: 'Erro', en: 'Error' },
  'record.saved': { pt: 'Salvo', en: 'Saved' },

  'record.si': { pt: 'SI {value}', en: 'SI {value}' },
  'record.total': { pt: 'Total', en: 'Total' },
  'record.strokesHint': {
    pt: 'Começa no par · {toPar} neste buraco',
    en: 'Starts at par · {toPar} on this hole',
  },
  /** Minúsculas de propósito: só entram em nome acessível, dentro de "Aumentar …". */
  'record.strokesLower': { pt: 'tacadas', en: 'strokes' },
  'record.puttsLower': { pt: 'putts', en: 'putts' },

  'record.par3NoFairway': { pt: 'Par 3 não tem fairway', en: 'A par 3 has no fairway' },
  'record.missed': { pt: 'Errou', en: 'Missed' },
  'record.hit': { pt: 'Acertou', en: 'Hit' },
  'record.automatic': { pt: '· automático', en: '· automatic' },
  'record.yes': { pt: '✓ Sim', en: '✓ Yes' },
  'record.no': { pt: '✕ Não', en: '✕ No' },

  'record.holeFilled': { pt: 'Buraco {number}, preenchido', en: 'Hole {number}, filled in' },
  'record.previous': { pt: 'Anterior', en: 'Previous' },
  'record.seeGrid': { pt: 'Ver a grade inteira', en: 'See the whole grid' },
  'record.saveAndReview': {
    pt: 'Salvar e revisar ({filled} de {total})',
    en: 'Save and review ({filled} of {total})',
  },

  // ------------------------------------------------------------- a grade

  'record.grid.strokesAria': {
    pt: 'Tacadas no buraco {number}, par {par}',
    en: 'Strokes on hole {number}, par {par}',
  },
  'record.grid.puttsAria': { pt: 'Putts no buraco {number}', en: 'Putts on hole {number}' },
  'record.grid.focusedCell': { pt: 'célula em foco', en: 'focused cell' },
  'record.grid.derivedNote': {
    pt: 'GIR e FW derivados — somente leitura',
    en: 'GIR and FW are derived — read only',
  },
  'record.grid.savesOnBlur': {
    pt: 'cada buraco salva ao sair da célula',
    en: 'each hole saves when you leave the cell',
  },
  'record.grid.filled': { pt: '{filled} de {total} preenchidos', en: '{filled} of {total} filled in' },
  'record.grid.oneAtATime': { pt: 'Um buraco por vez', en: 'One hole at a time' },

  // -------------------------------------------------------- passo 3: o flight

  'record.you': { pt: 'Você', en: 'You' },
  /** Minúscula porque só entra no meio de "Time de …", nome acessível do seletor A/B. */
  'record.youLower': { pt: 'você', en: 'you' },
  'record.teamOf': { pt: 'Time de {name}', en: '{name}’s team' },
  'record.startFailed': {
    pt: 'Não foi possível começar a volta',
    en: 'Could not start the round',
  },

  'record.whoPlayed': { pt: 'Quem jogou com você', en: 'Who played with you' },
  'record.whoPlayedQuestion': { pt: 'Quem jogou com você?', en: 'Who played with you?' },
  'record.roundContext': {
    pt: '{course} · {holes} buracos · Tee {tee}',
    en: '{course} · {holes} holes · Tee {tee}',
  },
  'record.flightMax': {
    pt: 'Um flight tem no máximo {max} jogadores — você é um deles',
    en: 'A flight holds at most {max} players — you are one of them',
  },
  'record.flightCount': { pt: 'Flight · {size} de {max}', en: 'Flight · {size} of {max}' },

  'record.addNthPlayer': { pt: 'Adicionar {ordinal} jogador', en: 'Add {ordinal} player' },
  'record.addPlayerSub': {
    pt: 'Dos seus amigos ou só o nome',
    en: 'From your friends, or just a name',
  },
  'record.removeFromFlight': { pt: 'Tirar {name} do flight', en: 'Remove {name} from the flight' },
  'record.noAccount': { pt: 'Sem conta', en: 'No account' },
  'record.playerReceives': {
    pt: { one: '{base} · recebe {count} tacada', other: '{base} · recebe {count} tacadas' },
    en: { one: '{base} · receives {count} stroke', other: '{base} · receives {count} strokes' },
  },

  'record.teams': { pt: 'Times', en: 'Teams' },
  'record.teamsAside': { pt: 'soma, para comparar', en: 'sum, to compare' },
  'record.tapToSwap': {
    pt: 'Toque num jogador para trocar de time',
    en: 'Tap a player to switch teams',
  },
  'record.teamName': { pt: 'Time {team}', en: 'Team {team}' },
  'record.swap': { pt: 'trocar', en: 'switch' },

  'record.gapWarning': {
    pt: 'Duplas desiguais: o Time {team} soma {amount} a mais.',
    en: 'Uneven pairs: Team {team} adds up to {amount} more.',
  },
  'record.gapAmount': { pt: '{value} de handicap', en: '{value} of handicap' },
  'record.gapBestBall': {
    pt: 'No best ball cada um usa o próprio, então a diferença aparece buraco a buraco.',
    en: 'In best ball each player uses their own, so the gap shows up hole by hole.',
  },
  'record.gapScramble': {
    pt: 'No scramble vale a soma da dupla, então a diferença entra direto no net.',
    en: 'In a scramble the pair’s sum counts, so the gap goes straight into the net score.',
  },

  /**
   * Os nomes de formato são termos da regra e ficam iguais nos dois idiomas. Continuam no catálogo
   * porque a prova de cobertura é o pseudo-locale: fora dele, "Scramble" na tela seria
   * indistinguível de uma string esquecida.
   */
  'record.contest': { pt: 'Disputa', en: 'Contest' },
  'record.individual': { pt: 'Cada um por si', en: 'Every player for themselves' },
  'record.pairs': { pt: 'Duplas', en: 'Pairs' },
  'record.teamFormat': { pt: 'Formato do time', en: 'Team format' },
  'record.bestBall': { pt: 'Best ball', en: 'Best ball' },
  'record.scramble': { pt: 'Scramble', en: 'Scramble' },
  'record.scoring': { pt: 'Contagem', en: 'Scoring' },
  'record.strokePlay': { pt: 'Stroke play', en: 'Stroke play' },
  'record.stableford': { pt: 'Stableford', en: 'Stableford' },
  'record.matchPlay': { pt: 'Match play', en: 'Match play' },
  'record.handicap': { pt: 'Handicap', en: 'Handicap' },
  'record.gross': { pt: 'Gross', en: 'Gross' },
  'record.net': { pt: 'Net', en: 'Net' },
  'record.startScorecard': { pt: 'Começar scorecard', en: 'Start the scorecard' },

  // ------------------------------------------------------ a folha "novo jogador"

  'record.newPlayer': { pt: 'Novo jogador', en: 'New player' },
  'record.justName': { pt: 'Só o nome', en: 'Just a name' },
  'record.noFriendsYet': {
    pt: 'Você ainda não tem amigos nem jogadores salvos. Use “Só o nome”.',
    en: 'You have no friends or saved players yet. Use “Just a name”.',
  },
  'record.name': { pt: 'Nome', en: 'Name' },
  /** Nome próprio de exemplo: não se traduz, mas é chave para o pseudo-locale enxergar. */
  'record.namePlaceholder': { pt: 'Diego Prado', en: 'Diego Prado' },
  'record.handicapIndex': { pt: 'Handicap index', en: 'Handicap index' },
  /** A vírgula decimal é o exemplo em português; em inglês, o ponto. */
  'record.handicapPlaceholder': { pt: '21,4', en: '21.4' },
  'record.receivesOnTee': {
    pt: { one: 'Recebe {count} tacada neste tee', other: 'Recebe {count} tacadas neste tee' },
    en: { one: 'Receives {count} stroke from this tee', other: 'Receives {count} strokes from this tee' },
  },
  'record.handicapOptional': {
    pt: 'Opcional — sem handicap o jogador fica de fora do resultado net',
    en: 'Optional — without a handicap the player is left out of the net result',
  },
  'record.handicapRange': {
    pt: 'Use um número entre -10 e 54',
    en: 'Use a number between -10 and 54',
  },
  'record.saveGuest': { pt: 'Salvar para as próximas', en: 'Save for next time' },
  'record.saveGuestSub': {
    pt: 'Fica na sua lista, sem criar conta para ele',
    en: 'Stays in your list, without creating an account for them',
  },
  'record.addFailed': { pt: 'Não foi possível adicionar', en: 'Could not add' },
  'record.addToFlight': { pt: 'Adicionar ao flight', en: 'Add to the flight' },

  // -------------------------------------------------- o cartão do flight

  'record.group.saveFailed': {
    pt: 'Não foi possível salvar o buraco',
    en: 'Could not save the hole',
  },
  'record.group.holeFacts': { pt: 'Par {par} · SI {si}', en: 'Par {par} · SI {si}' },
  'record.group.formatSuffix': { pt: ' · {format}', en: ' · {format}' },
  'record.group.teamTotal': { pt: 'time {team}', en: 'team {team}' },
  'record.group.yourTotal': { pt: 'seu total', en: 'your total' },
  'record.group.oneBallPerPair': { pt: 'Uma bola por dupla', en: 'One ball per pair' },
  'record.group.ownBall': {
    pt: 'Cada um joga a própria bola',
    en: 'Each player plays their own ball',
  },
  'record.group.strokesHere': { pt: 'Tacadas neste buraco', en: 'Strokes on this hole' },
  'record.group.gross': { pt: 'Bruto', en: 'Gross' },
  'record.group.receives': {
    pt: { one: '{count} tacada', other: '{count} tacadas' },
    en: { one: '{count} stroke', other: '{count} strokes' },
  },
  'record.group.hcp': { pt: 'hcp {value}', en: 'hcp {value}' },
  'record.group.teamStrokesAria': {
    pt: 'Tacadas do time {team} no buraco {number}',
    en: 'Strokes for team {team} on hole {number}',
  },
  'record.group.playerStrokesAria': {
    pt: 'Tacadas de {name} no buraco {number}',
    en: 'Strokes for {name} on hole {number}',
  },
  'record.group.bestOf': { pt: 'Melhor {mode}', en: 'Best {mode}' },
  'record.group.netLower': { pt: 'net', en: 'net' },
  'record.group.grossLower': { pt: 'gross', en: 'gross' },
  'record.group.teamPutts': { pt: 'Putts do time', en: 'Team putts' },
  'record.group.teamPuttsAria': {
    pt: 'Putts do seu time neste buraco',
    en: 'Putts for your team on this hole',
  },
  'record.group.puttsAria': { pt: 'Putts neste buraco', en: 'Putts on this hole' },
  'record.group.penaltyAria': {
    pt: 'Tacadas de penalidade neste buraco',
    en: 'Penalty strokes on this hole',
  },
  'record.group.bunkerAria': {
    pt: 'Tacadas de bunker neste buraco',
    en: 'Bunker shots on this hole',
  },
  'record.group.collapse': { pt: 'recolher ▴', en: 'collapse ▴' },

  // ------------------------------------------------- entrar num flight (2a)

  'record.finishOwnCardOnly': {
    pt: 'Você fecha só o seu cartão. A volta do grupo continua aberta até quem a começou encerrar.',
    en: 'You are closing only your own card. The group round stays open until whoever started it finishes.',
  },
  'record.join.title': { pt: 'Entrar na volta', en: 'Join the round' },
  'record.join.back': { pt: 'Voltar para a timeline', en: 'Back to the timeline' },
  'record.join.who': {
    pt: '{name} abriu esta volta e incluiu você',
    en: '{name} started this round and included you',
  },
  'record.join.where': {
    pt: '{course} · {holes} buracos · em andamento no buraco {hole}',
    en: '{course} · {holes} holes · in progress on hole {hole}',
  },
  'record.join.whereFresh': {
    pt: '{course} · {holes} buracos · a volta acabou de começar',
    en: '{course} · {holes} holes · the round has just started',
  },
  'record.join.tee': { pt: 'Seu tee', en: 'Your tee' },
  'record.join.teeWhy': {
    pt: 'O tee decide o seu course handicap — por isso a pergunta, mesmo já sabendo o campo.',
    en: 'The tee sets your course handicap — that is why we ask, even knowing the course.',
  },
  'record.join.firstNine': { pt: 'Primeira volta', en: 'First nine' },
  'record.join.secondNine': { pt: 'Segunda volta', en: 'Second nine' },
  'record.join.behindQuestion': {
    pt: 'Você jogou os buracos 1 a {last}?',
    en: 'Did you play holes 1 to {last}?',
  },
  'record.join.fromStart': {
    pt: 'Sim, jogo desde o início',
    en: 'Yes, I have been playing from the start',
  },
  'record.join.fromStartNote': {
    pt: 'Você preenche os {count} buracos agora e a volta conta para o seu handicap.',
    en: 'You fill in those {count} holes now and the round counts toward your handicap.',
  },
  'record.join.fromHere': {
    pt: 'Não, entro agora no {hole}',
    en: 'No, I am joining now at {hole}',
  },
  'record.join.fromHereNote': {
    pt: 'Seu cartão fica com {count} buracos e serve de registro, mas não conta para o handicap.',
    en: 'Your card ends with {count} holes and stands as a record, but does not count toward your handicap.',
  },
  'record.join.confirmCatchUp': {
    pt: 'Entrar e preencher 1–{last}',
    en: 'Join and fill in 1–{last}',
  },
  'record.join.confirm': { pt: 'Entrar na volta', en: 'Join the round' },
  'record.join.joining': { pt: 'Entrando…', en: 'Joining…' },
  'record.join.failed': { pt: 'Não foi possível entrar na volta', en: 'Could not join the round' },
  'record.join.already': {
    pt: 'Você já está nesta volta',
    en: 'You are already in this round',
  },
  'record.join.openScorecard': { pt: 'Abrir o cartão', en: 'Open the scorecard' },

  // ------------------------------------------------- a grade do flight

  'record.group.strokesShort': { pt: 'tac.', en: 'str.' },
  'record.group.legendStrokes': {
    pt: '“1 tac.” = tacadas de handicap',
    en: '“1 str.” = handicap strokes',
  },
  'record.group.legendBestBall': {
    pt: 'Linha de dupla = melhor gross dos dois',
    en: 'Pair row = the better gross of the two',
  },
  'record.group.puttsHoleAria': {
    pt: 'Putts no buraco {number}',
    en: 'Putts on hole {number}',
  },
  'record.group.detailCellAria': {
    pt: '{field} de {name} no buraco {number}',
    en: '{field} for {name} on hole {number}',
  },
  'record.grid.legendKeys': {
    pt: 'Tab entre jogadores · ← → entre buracos · Enter avança',
    en: 'Tab between players · ← → between holes · Enter advances',
  },
  'record.grid.notPlayed': { pt: 'não jogado', en: 'not played' },
  'record.grid.tagMarker': { pt: 'Marcador', en: 'Marker' },
  'record.grid.tagApp': { pt: 'No app', en: 'In the app' },
  'record.grid.tagNoAccount': { pt: 'Sem conta', en: 'No account' },
  'record.grid.puttsOnHole': { pt: 'Putts · buraco {number}', en: 'Putts · hole {number}' },
  'record.grid.fwPenBunker': { pt: 'FW · Pen · Bunker', en: 'FW · Pen · Bunker' },
  'record.grid.fwHiddenPar3': { pt: 'FW some em par 3', en: 'FW disappears on par 3' },
  'record.group.strokeIndexRow': { pt: 'Stroke index', en: 'Stroke index' },
  'record.group.saveAndExit': { pt: 'Salvar e sair', en: 'Save and exit' },
  'record.group.seeWholeCard': { pt: 'Ver o cartão inteiro', en: 'See the whole card' },
  /**
   * Os detalhes deixaram de ser só seus quando o amigo passou a poder entrar na volta: o marcador
   * abre a linha de qualquer jogador, então o título diz de quem ela é — e `record.group.expand`
   * saiu junto, porque a linha agora abre pelo toque no jogador e não por um "abrir" genérico.
   */
  'record.group.detailsOf': { pt: 'Detalhes de {name}', en: '{name} — details' },
  'record.group.openDetails': { pt: 'Abrir detalhes de {name}', en: 'Open details for {name}' },

  // --------------------------------------------------------- o quadro

  'standings.howToRead': { pt: 'Como ler o quadro', en: 'How to read the table' },
  'standings.position': { pt: 'Posição', en: 'Position' },
  'standings.player': { pt: 'Jogador', en: 'Player' },
  'standings.par': { pt: 'Par', en: 'Par' },
  'standings.net': { pt: 'Net', en: 'Net' },
  'standings.points': { pt: 'Pts', en: 'Pts' },
  'standings.pair': { pt: 'Dupla', en: 'Pair' },
  'standings.strokesShort': { pt: 'Tac.', en: 'Str.' },
  'standings.hcpShort': { pt: 'Hcp', en: 'Hcp' },
  'standings.hcp': { pt: 'hcp {value}', en: 'hcp {value}' },

  'standings.formatIndividual': {
    pt: '{players} jogadores · cada um por si · {scoring} · {handicap} — o formato escolhido no registro',
    en: '{players} players · every player for themselves · {scoring} · {handicap} — the format chosen at setup',
  },
  'standings.formatTeams': {
    pt: '{team} · {scoring} · {handicap} — o formato escolhido no registro',
    en: '{team} · {scoring} · {handicap} — the format chosen at setup',
  },
  'standings.sample': {
    pt: '{holes} buracos, {players} jogadores',
    en: '{holes} holes, {players} players',
  },
  'standings.countedHoles': {
    pt: { one: '· contou em {count} buraco', other: '· contou em {count} buracos' },
    en: { one: '· counted on {count} hole', other: '· counted on {count} holes' },
  },

  /**
   * O fecho do quadro. Cada ramo é uma frase inteira, e não um molde com pedaços trocáveis:
   * "você é um deles" e "você ficou em 3º" são orações diferentes, não variações de uma só.
   */
  'standings.tiedLeadYou': {
    pt: 'Empate na liderança entre {count} jogadores — você é um deles.',
    en: 'A {count}-way tie for the lead — you are one of them.',
  },
  'standings.tiedLead': {
    pt: '{count} jogadores empataram na liderança. Você ficou em {place}.',
    en: '{count} players tied for the lead. You finished {place}.',
  },
  'standings.grossVsView': {
    pt: 'No bruto você foi {grossPlace}; {view}, {viewPlace}.',
    en: 'On gross you were {grossPlace}; {view}, {viewPlace}.',
  },
  'standings.inNet': { pt: 'no net', en: 'on net' },
  'standings.inStableford': { pt: 'no stableford', en: 'on stableford' },
  'standings.youWon': { pt: 'Você ganhou a volta.', en: 'You won the round.' },
  'standings.otherWon': {
    pt: '{name} ganhou a volta. Você ficou em {place}.',
    en: '{name} won the round. You finished {place}.',
  },

  'standings.matchResult': { pt: 'Resultado', en: 'Result' },
  'standings.matchTied': { pt: 'Empatada', en: 'Tied' },
  'standings.matchEndedTied': {
    pt: 'A partida terminou empatada.',
    en: 'The match ended tied.',
  },
  'standings.yourPairWon': { pt: 'Sua dupla venceu', en: 'Your pair won' },
  'standings.teamWon': { pt: 'Time {team} venceu', en: 'Team {team} won' },
  'standings.matchDetail': {
    pt: {
      one: '{lead} up com {count} buraco para jogar — fechou no {hole}',
      other: '{lead} up com {count} buracos para jogar — fechou no {hole}',
    },
    en: {
      one: '{lead} up with {count} hole to play — decided at {hole}',
      other: '{lead} up with {count} holes to play — decided at {hole}',
    },
  },
  'standings.wonLostHalved': {
    pt: 'venceu {won} · perdeu {lost} · empatou {halved}',
    en: 'won {won} · lost {lost} · halved {halved}',
  },
  'standings.teamTook': { pt: 'Time {team} levou', en: 'Team {team} took it' },
  'standings.halved': { pt: 'Empatado', en: 'Halved' },
  'standings.notPlayed': { pt: 'Não jogado', en: 'Not played' },
  'standings.closedEarly': {
    pt: 'A partida fechou no {hole}, então os buracos {holes} não foram jogados.',
    en: 'The match was decided at {hole}, so holes {holes} were not played.',
  },
  'standings.wentToTheEnd': { pt: 'A partida foi até o fim.', en: 'The match went to the end.' },
  'standings.scrambleNoHandicap': {
    pt: ' No scramble a dupla joga uma bola só, então nenhuma volta entra no handicap — nem a sua.',
    en: ' In a scramble the pair plays a single ball, so no round counts towards a handicap — not even yours.',
  },

  'standings.teamsRound': { pt: 'Volta em duplas.', en: 'A pairs round.' },
  'standings.teamsTied': { pt: 'As duas duplas empataram.', en: 'The two pairs tied.' },
  'standings.marginPoints': {
    pt: { one: '{count} ponto', other: '{count} pontos' },
    en: { one: '{count} point', other: '{count} points' },
  },
  'standings.marginStrokes': {
    pt: { one: '{count} tacada', other: '{count} tacadas' },
    en: { one: '{count} stroke', other: '{count} strokes' },
  },
  'standings.yourPairTook': { pt: 'Sua dupla ganhou', en: 'Your pair won' },
  'standings.teamTookIt': { pt: 'O Time {team} ganhou', en: 'Team {team} won' },
  'standings.wonByScramble': {
    pt: '{won} por {margin}. No scramble nenhuma volta entra no handicap — nem a sua.',
    en: '{won} by {margin}. In a scramble no round counts towards a handicap — not even yours.',
  },
  'standings.wonBy': { pt: '{won} por {margin}.', en: '{won} by {margin}.' },
  'standings.youCarried': {
    pt: '{won} por {margin}. Você contou em {yours} buracos — {other} segurou os outros {theirs}.',
    en: '{won} by {margin}. You counted on {yours} holes — {other} held the other {theirs}.',
  },
  'standings.theyCarried': {
    pt: '{won} por {margin}. {name} contou em {yours} buracos — você segurou os outros {theirs}.',
    en: '{won} by {margin}. {name} counted on {yours} holes — you held the other {theirs}.',
  },
  /** "3, 5 e 7" — a conjunção final é a única parte que muda de idioma. */
  'standings.listAnd': { pt: '{list} e {last}', en: '{list} and {last}' },
} as const satisfies Catalog;
